import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Database, ArrowUp, ArrowDown, Plus, Trash2, CheckCircle, AlertTriangle, RotateCcw } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';

interface Migration {
    id: number;
    version: string;
    name: string;
    sql: string;
    status: 'pending' | 'applied' | 'rolled-back';
}

const INITIAL_MIGRATIONS: Migration[] = [
    { id: 1, version: '001', name: 'create_users_table', sql: 'CREATE TABLE users (id SERIAL, name TEXT);', status: 'applied' },
    { id: 2, version: '002', name: 'add_email_column', sql: 'ALTER TABLE users ADD COLUMN email TEXT;', status: 'applied' },
];

export const DbMigrations = () => {
    const { completeModule } = useAcademyStore();
    const [migrations, setMigrations] = useState(INITIAL_MIGRATIONS);
    const [newMigrationName, setNewMigrationName] = useState('');
    const [newMigrationSql, setNewMigrationSql] = useState('');
    const [currentVersion, setCurrentVersion] = useState(2);
    const [log, setLog] = useState<string[]>(['Database initialized at version 002']);
    const [operations, setOperations] = useState({ up: 0, down: 0, create: 0 });
    const [completed, setCompleted] = useState(false);

    const addLog = (msg: string) => setLog(prev => [...prev.slice(-5), `[${new Date().toLocaleTimeString()}] ${msg}`]);

    const createMigration = () => {
        if (!newMigrationName || !newMigrationSql) return;
        const version = String(migrations.length + 1).padStart(3, '0');
        const newMig: Migration = {
            id: Date.now(),
            version,
            name: newMigrationName.toLowerCase().replace(/\s+/g, '_'),
            sql: newMigrationSql,
            status: 'pending',
        };
        setMigrations([...migrations, newMig]);
        addLog(`Created migration: ${version}_${newMig.name}`);
        setNewMigrationName('');
        setNewMigrationSql('');
        setOperations(o => ({ ...o, create: o.create + 1 }));
    };

    const migrateUp = () => {
        const pending = migrations.find(m => m.status === 'pending');
        if (!pending) return;

        setMigrations(migrations.map(m =>
            m.id === pending.id ? { ...m, status: 'applied' } : m
        ));
        setCurrentVersion(parseInt(pending.version));
        addLog(`⬆️ Migrated UP to ${pending.version}: ${pending.name}`);
        setOperations(o => ({ ...o, up: o.up + 1 }));
    };

    const migrateDown = () => {
        const applied = [...migrations].reverse().find(m => m.status === 'applied');
        if (!applied || applied.version === '001') return; // Don't rollback initial migration

        setMigrations(migrations.map(m =>
            m.id === applied.id ? { ...m, status: 'rolled-back' } : m
        ));
        setCurrentVersion(parseInt(applied.version) - 1);
        addLog(`⬇️ Rolled BACK from ${applied.version}: ${applied.name}`);
        setOperations(o => ({ ...o, down: o.down + 1 }));
    };

    const reset = () => {
        setMigrations(INITIAL_MIGRATIONS);
        setCurrentVersion(2);
        setLog(['Database reset to initial state']);
        setOperations({ up: 0, down: 0, create: 0 });
    };

    // Check completion
    useEffect(() => {
        if (operations.create >= 1 && operations.up >= 1 && operations.down >= 1 && !completed) {
            setCompleted(true);
            completeModule('db-migrations', 100);
        }
    }, [operations, completed, completeModule]);

    const pendingCount = migrations.filter(m => m.status === 'pending').length;
    const appliedCount = migrations.filter(m => m.status === 'applied').length;

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 font-sans">
            <div className="bg-white shadow-sm px-8 py-4 flex justify-between items-center sticky top-0 z-30">
                <Link to="/" className="flex items-center gap-2 text-slate-500 font-bold hover:text-blue-600">
                    <Home size={20} /> Dashboard
                </Link>
                <div className="flex items-center gap-4">
                    {completed && <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1"><CheckCircle size={14} /> Complete</div>}
                    <div className="bg-blue-100 text-blue-800 px-4 py-1 rounded-full text-sm font-bold">
                        🔄 Database Migrations
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-black font-comic text-slate-800 mb-2">
                        <Database className="inline text-blue-600 mr-3" size={40} />
                        The Time Machine
                    </h1>
                    <p className="text-slate-600 max-w-2xl mx-auto">
                        Migrations let you <strong>version control your database</strong>.
                        Move forward (UP) to apply changes, or backward (DOWN) to undo them!
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Migration List */}
                    <div className="lg:col-span-2 bg-white rounded-3xl p-6 shadow-xl border-2 border-slate-100">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="font-bold text-slate-700 flex items-center gap-2">
                                <Database size={18} /> Migration History
                            </h3>
                            <div className="text-sm">
                                <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full font-bold mr-2">{appliedCount} Applied</span>
                                <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full font-bold">{pendingCount} Pending</span>
                            </div>
                        </div>

                        <div className="space-y-3">
                            <AnimatePresence>
                                {migrations.map((m, i) => (
                                    <motion.div
                                        key={m.id}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        className={`p-4 rounded-xl border-2 ${m.status === 'applied' ? 'bg-green-50 border-green-200' :
                                            m.status === 'pending' ? 'bg-yellow-50 border-yellow-200' :
                                                'bg-slate-50 border-slate-200 opacity-60'
                                            }`}
                                    >
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <div className="font-mono font-bold text-slate-800">
                                                    {m.version}_{m.name}
                                                </div>
                                                <div className="text-xs font-mono text-slate-500 mt-1 bg-slate-100 px-2 py-1 rounded">
                                                    {m.sql}
                                                </div>
                                            </div>
                                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${m.status === 'applied' ? 'bg-green-500 text-white' :
                                                m.status === 'pending' ? 'bg-yellow-500 text-white' :
                                                    'bg-slate-400 text-white'
                                                }`}>
                                                {m.status.toUpperCase()}
                                            </span>
                                        </div>
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>

                        {/* Controls */}
                        <div className="mt-6 flex gap-3">
                            <button
                                onClick={migrateUp}
                                disabled={pendingCount === 0}
                                className="flex-1 bg-green-500 text-white py-3 rounded-xl font-bold hover:bg-green-600 disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                <ArrowUp size={18} /> Migrate UP
                            </button>
                            <button
                                onClick={migrateDown}
                                disabled={appliedCount <= 1}
                                className="flex-1 bg-red-500 text-white py-3 rounded-xl font-bold hover:bg-red-600 disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                <ArrowDown size={18} /> Rollback
                            </button>
                            <button onClick={reset} className="px-4 py-3 bg-slate-200 hover:bg-slate-300 rounded-xl">
                                <RotateCcw size={18} />
                            </button>
                        </div>
                    </div>

                    {/* Create Migration */}
                    <div className="space-y-6">
                        <div className="bg-white rounded-3xl p-6 shadow-xl border-2 border-slate-100">
                            <h3 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
                                <Plus className="text-blue-500" /> Create Migration
                            </h3>

                            <div className="space-y-3">
                                <input
                                    value={newMigrationName}
                                    onChange={e => setNewMigrationName(e.target.value)}
                                    placeholder="Migration name..."
                                    className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 outline-none"
                                />
                                <textarea
                                    value={newMigrationSql}
                                    onChange={e => setNewMigrationSql(e.target.value)}
                                    placeholder="SQL statement..."
                                    rows={3}
                                    className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 outline-none font-mono text-sm"
                                />
                                <button
                                    onClick={createMigration}
                                    disabled={!newMigrationName || !newMigrationSql}
                                    className="w-full bg-blue-500 text-white py-3 rounded-xl font-bold hover:bg-blue-600 disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    <Plus size={18} /> Create Migration
                                </button>
                            </div>
                        </div>

                        {/* Log */}
                        <div className="bg-slate-900 rounded-3xl p-6 shadow-xl">
                            <h3 className="font-bold text-green-400 mb-4">Migration Log</h3>
                            <div className="font-mono text-xs text-green-400 space-y-1">
                                {log.map((entry, i) => (
                                    <div key={i} className="opacity-80">{entry}</div>
                                ))}
                            </div>
                            <div className="mt-4 pt-4 border-t border-slate-700 text-slate-500 text-xs">
                                Current DB Version: <span className="text-white font-bold">{String(currentVersion).padStart(3, '0')}</span>
                            </div>
                            <div className="mt-4 text-yellow-500/80 text-xs italic">
                                💡 Mission: Create 1, Migrate UP, then Rollback DOWN!
                            </div>
                        </div>
                    </div>
                </div>

                {/* Celebration */}
                {completed && (
                    <ModuleSuccess
                        title="Time Traveler"
                        onRetry={() => {
                            setCompleted(false);
                            setOperations({ up: 0, down: 0, create: 0 });
                        }}
                    >
                        <div className="bg-slate-100 rounded-2xl p-6 text-left border-2 border-slate-200">
                            <h4 className="font-bold text-slate-800 mb-2">🕰️ History Rewritten</h4>
                            <p className="text-slate-600 mb-4 text-sm">
                                You've mastered Database Version Control.
                            </p>
                            <ul className="space-y-2 text-xs font-mono text-slate-600">
                                <li>• <strong>UP</strong>: Applying changes (<code>db:migrate</code>)</li>
                                <li>• <strong>DOWN</strong>: Undoing mistakes (<code>db:rollback</code>)</li>
                                <li>• Always review SQL before running!</li>
                            </ul>
                        </div>
                    </ModuleSuccess>
                )}
            </div>
        </div>
    );
};

export default DbMigrations;
